import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SignaturePad from "./SignaturePad";
import { useMediaQuery } from "./UI";
import {
  LayoutDashboard, Map, Inbox, MapPin, Wallet, Ruler, Receipt, Building2,
  Clock, HardHat, Truck, Telescope, Menu, X, LogOut, PanelLeftClose, PanelLeft,
  ChevronDown, Search, PenLine,
} from "lucide-react";

/* Twelve flat links is a wall. Grouped, it reads as four short lists. */
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
      { path: "/admin/locations", label: "Site Map", icon: Map },
      { path: "/admin/inquiries", label: "Inquiries", icon: Inbox },
    ],
  },
  {
    label: "Operations",
    items: [
      { path: "/admin/sites", label: "Sites", icon: MapPin },
      { path: "/admin/drawings", label: "Drawings", icon: Ruler },
      { path: "/admin/attendance", label: "Attendance", icon: Clock },
      { path: "/admin/expenses", label: "Expenses", icon: Wallet },
    ],
  },
  {
    label: "Business",
    items: [
      { path: "/admin/bills", label: "Billing", icon: Receipt },
      { path: "/admin/clients", label: "Clients", icon: Building2 },
    ],
  },
  {
    label: "Resources",
    items: [
      { path: "/admin/crews", label: "Crews", icon: HardHat },
      { path: "/admin/vehicles", label: "Vehicles", icon: Truck },
      { path: "/admin/instruments", label: "Instruments", icon: Telescope },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap(g => g.items);
const SIDEBAR_KEY = "hiland.sidebar.collapsed";

/* Between 1024 and 1440 the rail has appeared but the viewport hasn't grown
   enough to pay the 256px it costs. Left expanded, a 1024px screen keeps only
   704px for content — less than the 975px it had at 1023px, so the layout would
   shrink as the window grew. Forcing the icon rail across this band keeps the
   content width climbing and leaves room for real tables:

     768 → 720 · 1023 → 975 · 1024 → 900 · 1280 → 1140 · 1439 → 1299
     1440 → 1120 (rail expands, and the toggle can win it back) · 1920 → 1480

   The boundary is 1440 rather than xl because expanding the rail always costs
   ~180px — this is the first width where paying it still leaves a generous
   column. Deliberately not a Tailwind breakpoint; it answers to the rail's
   width, not to the type scale.

   The rail is the *resting* state in this band, not the only one: the toggle
   still expands it, as an overlay that floats over the content instead of
   pushing it. That keeps the labels reachable on a tablet — where icon-only
   nav is hardest to read and there is no hover tooltip to fall back on —
   without giving back any of the content width the band exists to protect. */
const COMPACT_RAIL = "(min-width: 1024px) and (max-width: 1439.98px)";

const matches = (pathname, path, exact) =>
  exact ? pathname === path : pathname.startsWith(path);

/* ── one nav row ── */
function NavLink({ item, showLabel, active }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      title={showLabel ? undefined : item.label}
      aria-current={active ? "page" : undefined}
      className={`group relative flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150
        ${showLabel ? "px-3 py-2.5" : "py-2.5 justify-center"}
        ${active ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:text-ink-900 hover:bg-ink-100/80"}`}
    >
      <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-brand-600 transition-all
        ${active ? "h-5 opacity-100" : "h-0 opacity-0"}`} />
      <Icon size={18} strokeWidth={active ? 2.4 : 2} className="shrink-0" />
      {showLabel && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

/* ── brand lockup ── */
function Brand({ showLabel, bordered = true }) {
  return (
    <Link to="/admin" className={`flex items-center gap-3 h-16 shrink-0 ${showLabel ? "px-4" : "justify-center"} ${bordered ? "border-b border-ink-100" : ""}`}>
      <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 grid place-items-center shadow-xs shrink-0">
        <span className="text-white font-bold text-sm font-display">H</span>
      </span>
      {showLabel && (
        <span className="min-w-0">
          <span className="block text-[15px] font-bold text-ink-900 font-display leading-tight truncate">HILAND</span>
          <span className="block text-[11px] text-ink-400 leading-tight">Surveyors · Admin</span>
        </span>
      )}
    </Link>
  );
}

/* ── nav + account block, shared by the desktop rail and the mobile drawer ── */
function SidebarBody({ showLabel, pathname, user, onLogout, onSignature }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { setMenuOpen(false); }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  return (
    <>
      <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-4 space-y-5 no-scrollbar">
        {NAV_GROUPS.map(group => (
          <div key={group.label} className="space-y-1">
            {showLabel
              ? <p className="px-3 pb-1 text-[10px] font-bold text-ink-400 uppercase tracking-[0.12em]">{group.label}</p>
              : <div className="h-px bg-ink-100 mx-2 mb-2" />}
            {group.items.map(item => (
              <NavLink key={item.path} item={item} showLabel={showLabel} active={matches(pathname, item.path, item.exact)} />
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-ink-100 p-3 shrink-0">
        {showLabel ? (
          <div className="relative" ref={menuRef}>
            <button onClick={() => setMenuOpen(v => !v)} aria-expanded={menuOpen}
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-ink-100 transition-colors text-left">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white grid place-items-center text-xs font-bold shrink-0">
                {user?.name?.[0]?.toUpperCase() || "A"}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-ink-800 truncate">{user?.name || "Admin"}</span>
                <span className="block text-[11px] text-ink-400 truncate">{user?.email || "Administrator"}</span>
              </span>
              <ChevronDown size={15} className={`text-ink-400 shrink-0 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            {menuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-ink-200 rounded-xl shadow-pop p-1 animate-fade-up z-10">
                <button onClick={() => { setMenuOpen(false); onSignature(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-ink-700 hover:bg-ink-100 transition-colors">
                  <PenLine size={16} className="text-ink-400" />
                  <span className="flex-1 text-left">Signature &amp; Seal</span>
                  {user?.signatureUrl && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Signature saved" />}
                </button>
                <div className="h-px bg-ink-100 my-1" />
                <button onClick={onLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            <button onClick={onSignature} title="Signature &amp; seal"
              className="w-full grid place-items-center py-2.5 rounded-xl text-ink-400 hover:text-brand-600 hover:bg-brand-50 transition-colors">
              <PenLine size={18} />
            </button>
            <button onClick={onLogout} title="Sign out"
              className="w-full grid place-items-center py-2.5 rounded-xl text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * fullHeight — the shell becomes a fixed 100dvh column and the page itself
 * never scrolls; the child owns its own internal scrolling. Listing screens
 * use it so filters and the pager stay put while only rows move.
 */
export default function AdminLayout({ children, fullHeight = false }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [prefCollapsed, setPrefCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === "1");
  // Tablet-only: a peek at the labels. Not persisted — it closes on navigate,
  // so restoring it on the next page load would fight the rail's resting state.
  const [railPeek, setRailPeek] = useState(false);

  const compactRail = useMediaQuery(COMPACT_RAIL);
  // On tablet the rail rests as icons and expands as an overlay; above the band
  // the width is the user's standing choice
  const collapsed = compactRail ? !railPeek : prefCollapsed;
  // What the content is padded for. On tablet that stays the icon rail even
  // while the peek is open — the peek floats, so it must not reflow the page.
  const gutterCollapsed = compactRail || prefCollapsed;

  // Deepest matching route wins, so /admin/sites/:id still highlights "Sites"
  const current = [...ALL_ITEMS].reverse().find(i => matches(pathname, i.path, i.exact));

  useEffect(() => { setDrawerOpen(false); setRailPeek(false); }, [pathname]);

  // Leaving the band mid-peek would otherwise strand the flag: at ≥1440 it is
  // ignored, and coming back to tablet would re-open the overlay unbidden
  useEffect(() => { if (!compactRail) setRailPeek(false); }, [compactRail]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setDrawerOpen(false); setRailPeek(false); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const toggleCollapse = () => {
    if (compactRail) { setRailPeek(p => !p); return; }   // transient, not a stored preference
    setPrefCollapsed(c => { localStorage.setItem(SIDEBAR_KEY, c ? "0" : "1"); return !c; });
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const openSignature = () => { setDrawerOpen(false); setSignatureOpen(true); };

  return (
    <div className={fullHeight
      ? "h-[100dvh] overflow-hidden flex flex-col bg-[var(--app-bg)]"
      : "min-h-screen bg-[var(--app-bg)]"}>

      {/* ══ Desktop rail ══ */}
      {/* Scrim for the tablet peek only — it sits under the rail and over the
          content, so a tap anywhere outside puts the rail back */}
      {railPeek && (
        <div className="hidden lg:block fixed inset-0 z-40 bg-ink-900/25 animate-fade-in"
          onClick={() => setRailPeek(false)} aria-hidden="true" />
      )}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 bg-white border-r border-ink-200/80
                    transition-[width] duration-200 ease-smooth
                    ${collapsed ? "w-[76px]" : "w-64"}
                    ${railPeek ? "z-40 shadow-pop" : "z-40"}`}
      >
        <Brand showLabel={!collapsed} />
        <SidebarBody showLabel={!collapsed} pathname={pathname} user={user} onLogout={handleLogout} onSignature={openSignature} />
        <button onClick={toggleCollapse} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-expanded={!collapsed}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-ink-200 shadow-xs
                     grid place-items-center text-ink-400 hover:text-brand-600 hover:border-brand-300 transition-all">
          {collapsed ? <PanelLeft size={12} /> : <PanelLeftClose size={12} />}
        </button>
      </aside>

      {/* ══ Mobile app bar ══ */}
      <header className={`lg:hidden z-40 bg-white/90 backdrop-blur-md border-b border-ink-200/80 safe-top ${
        fullHeight ? "shrink-0" : "sticky top-0"}`}>
        <div className="h-14 px-2 sm:px-3 flex items-center gap-1.5">
          <button onClick={() => setDrawerOpen(true)} aria-label="Open menu"
            className="w-10 h-10 grid place-items-center rounded-xl text-ink-600 hover:bg-ink-100 active:scale-95 transition-all">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 grid place-items-center shrink-0">
              <span className="text-white font-bold text-[11px] font-display">H</span>
            </span>
            <span className="font-bold text-ink-900 font-display text-[15px] truncate">
              {current?.label || "HILAND"}
            </span>
          </div>
          <Link to="/admin/sites" aria-label="Find a site"
            className="w-10 h-10 grid place-items-center rounded-xl text-ink-500 hover:bg-ink-100 active:scale-95 transition-all">
            <Search size={18} />
          </Link>
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white grid place-items-center text-xs font-bold shrink-0">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </span>
        </div>
      </header>

      {/* ══ Mobile drawer ══ */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 bg-ink-900/45 backdrop-blur-[2px] animate-fade-in" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[82%] max-w-[300px] bg-white flex flex-col shadow-pop animate-slide-in-left safe-top">
            <div className="flex items-center justify-between border-b border-ink-100 pr-1">
              <Brand showLabel bordered={false} />
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu"
                className="w-10 h-10 grid place-items-center rounded-xl text-ink-400 hover:bg-ink-100 transition-colors shrink-0">
                <X size={19} />
              </button>
            </div>
            <SidebarBody showLabel pathname={pathname} user={user} onLogout={handleLogout} onSignature={openSignature} />
          </aside>
        </div>
      )}

      <SignaturePad open={signatureOpen} onClose={() => setSignatureOpen(false)} />

      {/* ══ Content ══ */}
      <main className={`transition-[padding] duration-200 ease-smooth ${gutterCollapsed ? "lg:pl-[76px]" : "lg:pl-64"} ${
        fullHeight ? "flex-1 min-h-0 flex flex-col" : "min-h-screen"}`}>
        {/* Gutters stay at 24px until xl — widening them at lg would take space
            from the band that has the least of it. The 1800px cap keeps ~1480px
            of content on a 1920 screen instead of stranding 320px per side. */}
        <div className={`mx-auto w-full max-w-[1800px] px-4 sm:px-6 xl:px-8 ${
          fullHeight ? "flex-1 min-h-0 flex flex-col py-4 sm:py-5" : "py-5 sm:py-7 pb-16"}`}>
          {children}
        </div>
      </main>
    </div>
  );
}
